import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Text,
  Box,
  FormControl,
  FormLabel,
  Button,
  VStack,
  useToast,
  Input,
} from '@chakra-ui/react';

import { AttachmentIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import FileUploadCard from 'components/FileUploadCard';

import {
  apiUploadCommercialFile,
  apiGetCommercialFiles,
  apiDeleteCommercialFile,
} from 'api';
import { COMMERCIAL_FILE_TYPES } from 'constants/fileTypes';
import type { APIApplicationFile } from 'types/api.types';

interface CommercialQuoteUploadProps {
  isLocked?: boolean;
}

const CommercialQuoteUpload = ({
  isLocked = false,
}: CommercialQuoteUploadProps) => {
  const { uuid } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Query for existing files
  const { data: files = [] } = useQuery({
    queryKey: ['commercial-files', uuid],
    queryFn: () => apiGetCommercialFiles(uuid!),
    refetchOnWindowFocus: false,
  }) as { data: APIApplicationFile[] };

  // Filter for quote files
  const filteredFiles = files.filter(
    (file) => file.file_type === COMMERCIAL_FILE_TYPES.QUOTE,
  );

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file, fileType }: { file: File; fileType: string }) =>
      apiUploadCommercialFile(uuid!, file, fileType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commercial-files', uuid] });
      toast({
        title: 'Success',
        description: 'Quote file uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload failed',
        description:
          error?.response?.data?.error ||
          'Failed to upload file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => apiDeleteCommercialFile(uuid!, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commercial-files', uuid] });
      toast({
        title: 'File deleted',
        description: 'Quote file removed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title: 'Delete failed',
        description: 'Failed to delete file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    uploadMutation.mutate({ file, fileType: 'quote' });

    // Clear the input
    event.target.value = '';
  };

  const handleRemoveFile = (fileId: number) => {
    deleteMutation.mutate(fileId);
  };

  return (
    <Card mb="20px" id="sectionCommercialQuoteUpload">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Quote File Upload
      </Text>

      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel fontSize="sm" fontWeight="500" ml="0">
            Quote Document
          </FormLabel>

          <VStack spacing={3} align="stretch">
            <Box>
              <Input
                type="file"
                onChange={handleFileSelect}
                display="none"
                id="quote-upload"
                disabled={uploadMutation.isPending}
                accept=".pdf,.eml,.msg"
              />
              <label htmlFor="quote-upload">
                <Button
                  as="span"
                  leftIcon={<AttachmentIcon />}
                  colorScheme="blue"
                  variant="outline"
                  size="md"
                  isLoading={uploadMutation.isPending}
                  loadingText="Uploading..."
                  cursor="pointer"
                  width="full"
                  maxW="300px"
                  isDisabled={isLocked}
                >
                  Choose File
                </Button>
              </label>
            </Box>

            {filteredFiles.map((file) => (
              <FileUploadCard
                key={file.id}
                file={file}
                badgeText="Quote"
                badgeColorScheme="green"
                onDelete={handleRemoveFile}
                isDeleting={deleteMutation.isPending}
                isLocked={isLocked}
              />
            ))}
          </VStack>
        </FormControl>
      </VStack>
    </Card>
  );
};

export default CommercialQuoteUpload;
